import { MdOutlineDelete } from "react-icons/md";
import {
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useDisclosure,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FiEdit2, FiMoreHorizontal, FiDownload } from "react-icons/fi";
import { TbApps } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import React, { useEffect, useState } from "react";
import { fetchApplications } from "../../services/state/applications/applications-async-calls";
import { useNavigate } from "react-router-dom";
import { getAxiosClient } from "../../services/HttpsService";
import { Application } from "@/services/state/applications/applications-interface";
import { JsxElement } from "typescript";

export const useApplications = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const {
    isOpen: isApplicationEditModelOpen,
    onOpen: onApplicationEditModeOpen,
    onClose: onApplicationEditModeClose,
  } = useDisclosure();

  const {
    isOpen: isApplicationDeleteModelOpen,
    onOpen: onApplicationDeleteModeOpen,
    onClose: onApplicationDeleteModeClose,
  } = useDisclosure();

  const {
    isOpen: isAddApplicationModalOpen,
    onOpen: onAddApplicationModalOpen,
    onClose: onAddApplicationModalClose,
  } = useDisclosure();

  const {
    isOpen: isImportApplicationModalOpen,
    onOpen: onImportApplicationModalOpen,
    onClose: onImportApplicationModalClose,
  } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<Application>();
  const [edit, setEdit] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const applications = useSelector(
    (state: RootState) => state.applications.applications
  );

  useEffect(() => {
    dispatch(fetchApplications());
  }, []);

  const configureAnalytics = [
    {
      title: "New Application",
      icon: TbApps,
      onClick: onAddApplicationModalOpen,
      enabled: true,
    },
  ];

  const onApplicationEditClick = (id: Number) => {
    console.log("onAppEditClick", id);
    setSelectedApplication(applications.find((app) => app.id === id));
    setEdit(true);
    onApplicationEditModeOpen();
  };

  const onApplicationImportClick = async (file: File) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await getAxiosClient().post(
        "/import-export/import/application",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        toast({
          title: "Import Successful",
          description: "The application has been successfully imported.",
          position: "top",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        dispatch(fetchApplications()); // Refresh the application list
        onImportApplicationModalClose();
      } else {
        toast({
          title: "Import Failed",
          description: "An error occurred while importing the application.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error importing application:", error);
      toast({
        title: "Import Failed",
        description: "An error occurred while importing the application.",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const onApplicationExportClick = async (uuid: string, appName: string) => {
    try {
      setLoading(true);
      const response = await getAxiosClient().get(
        `/import-export/export/application/${uuid}`,
        {
          responseType: "blob", // Important for file downloads
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Set filename - you might want to get this from response headers
      link.setAttribute("download", `${appName}_export.zip`); // Adjust extension as needed

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: "The application has been successfully exported.",
        position: "top",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error exporting application:", error);
      toast({
        title: "Export Failed",
        description: "An error occurred while exporting the application.",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const onApplicationDeleteClick = async (id: Number) => {
    try {
      setLoading(true);
      const response = await getAxiosClient().delete(
        `/api/eza_app_application/softDelete/${id}`
      ); // Adjust the URL as per your API endpoint

      if (response.status === 204) {
        // HTTP 204 NO_CONTENT
        toast({
          title: "Application Deleted",
          description: "The application has been successfully deleted.",
          position: "top",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        dispatch(fetchApplications()); // Refresh the application list
      } else {
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting application:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the application.",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Prefix",
      accessor: "prefix",
    },
    {
      Header: "Description",
      accessor: "description",
    },
    {
      Header: "",
      accessor: "actions",
    },
  ];

  const sortedApplications = [...applications].sort(
    (a, b) => new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime()
  );

  // Filter applications based on search term
  const filteredApplications = sortedApplications.filter((application) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      application.appName?.toLowerCase().includes(searchLower) ||
      application.appPrefix?.toLowerCase().includes(searchLower) ||
      application.appDesc?.toLowerCase().includes(searchLower)
    );
  });

  const columnData = filteredApplications.map((application) => {
    return {
      name: (
        <Text
          onClick={() =>
            navigate(`/platform/design/objects/${application.uuid}`)
          }
          color="brand.500"
          cursor="pointer"
          _hover={{
            color: "brand.700",
            // textDecoration: "underline",
          }}
          transition="all 0.2s ease"
          fontWeight="semibold"
          textUnderlineOffset="2px"
        >
          {application.appName}
        </Text>
      ),
      prefix: application.appPrefix.toUpperCase(),
      description: application.appDesc,
      actions: (
        <Menu placement="bottom-end">
          <MenuButton
            as={IconButton as React.ElementType}
            aria-label="Actions"
            icon={
              <Icon as={FiMoreHorizontal as React.ElementType} boxSize="18px" />
            }
            variant="outline"
            size="sm"
            colorScheme="gray"
            bg="rgba(255, 255, 255, 0.1)"
            borderColor="rgba(255, 255, 255, 0.3)"
            color="white"
            _hover={{
              bg: "rgba(255, 255, 255, 0.2)",
              borderColor: "brand.400",
            }}
            borderRadius={"md"}
          />
          <Portal>
            <MenuList
              bg="rgba(0, 0, 0, 0.95)"
              backdropFilter="blur(20px)"
              border="1px solid rgba(255, 255, 255, 0.4)"
              borderRadius="8px"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.5)"
              zIndex={9999}
              minW="140px"
            >
              <MenuItem
                icon={<Icon as={FiEdit2 as React.ElementType} boxSize="18px" />}
                onClick={() => onApplicationEditClick(application.id)}
                bg="transparent"
                color="white"
                _hover={{
                  bg: "rgba(118, 82, 247, 0.2)",
                  color: "white",
                }}
                _focus={{
                  bg: "rgba(118, 82, 247, 0.2)",
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                icon={
                  <Icon as={FiDownload as React.ElementType} boxSize="18px" />
                }
                onClick={() =>
                  onApplicationExportClick(application.uuid, application.appName)
                }
                bg="transparent"
                color="white"
                _hover={{
                  bg: "rgba(118, 82, 247, 0.2)",
                  color: "white",
                }}
                _focus={{
                  bg: "rgba(118, 82, 247, 0.2)",
                }}
              >
                Export
              </MenuItem>
              <MenuItem
                icon={
                  <Icon
                    as={MdOutlineDelete as React.ElementType}
                    boxSize="18px"
                  />
                }
                onClick={() => onApplicationDeleteClick(application.id)}
                bg="transparent"
                color="white"
                _hover={{
                  bg: "rgba(220, 38, 38, 0.2)",
                  color: "red.300",
                }}
                _focus={{
                  bg: "rgba(220, 38, 38, 0.2)",
                }}
              >
                Delete
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      ),
    };
  });

  return {
    configureAnalytics,
    columns,
    columnData,
    isAddApplicationModalOpen,
    onAddApplicationModalOpen,
    onAddApplicationModalClose,
    isApplicationEditModelOpen,
    onApplicationEditModeClose,
    selectedApplication,
    edit,
    loading,
    isImportApplicationModalOpen,
    onImportApplicationModalOpen,
    onImportApplicationModalClose,
    onApplicationImportClick,
    searchTerm,
    setSearchTerm,
    filteredApplications,
    applications,
  };
};
